<template>
  <div class="whatsapp-container">
    <!-- Loader enquanto verifica conexão -->
    <div v-if="checkingConnection" class="loading-state">
      <div class="loading-content">
        <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-3 text-muted">Verificando configuração do WhatsApp...</p>
      </div>
    </div>

    <!-- Tela de configuração -->
    <div v-else-if="!connectionConfigured" class="empty-config-state">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8 col-xl-7">
            <div class="card shadow-sm border-0">
              <div class="card-body p-5">
                <div class="text-center mb-4">
                  <div class="whatsapp-icon-wrapper mb-4">
                    <div class="whatsapp-icon">
                      <i class="bi bi-whatsapp"></i>
                    </div>
                    <div class="icon-pulse"></div>
                  </div>
                  
                  <h2 class="empty-config-title mb-3">Conecte seu WhatsApp Business</h2>
                  <p class="empty-config-description mb-4">
                    Configure a integração com WhatsApp Cloud API para automatizar o atendimento aos pacientes através da nossa IA.
                  </p>
                </div>

                <div class="alert alert-warning border-warning mb-4">
                  <div class="d-flex align-items-start">
                    <i class="bi bi-exclamation-triangle fs-2 me-3 mt-1"></i>
                    <div>
                      <strong class="d-block mb-2">Importante: Use número da clínica</strong>
                      <p class="mb-0 small">
                        Esta funcionalidade é <strong>recomendada apenas para número comercial da clínica</strong>. 
                        Não use seu número pessoal, pois ao conectar o WhatsApp Business API, o número será desvinculado do WhatsApp no celular 
                        e funcionará exclusivamente através do sistema.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="features-list mb-4">
                  <div class="feature-item">
                    <div class="feature-icon">
                      <i class="bi bi-cpu"></i>
                    </div>
                    <div class="feature-content">
                      <strong>Atendimento automatizado com IA</strong>
                      <p class="mb-0 small text-muted">Respostas inteligentes e agendamento automático</p>
                    </div>
                  </div>
                  <div class="feature-item">
                    <div class="feature-icon">
                      <i class="bi bi-chat-dots"></i>
                    </div>
                    <div class="feature-content">
                      <strong>Mensagens em tempo real</strong>
                      <p class="mb-0 small text-muted">Receba e envie mensagens instantaneamente</p>
                    </div>
                  </div>
                  <div class="feature-item">
                    <div class="feature-icon">
                      <i class="bi bi-file-text"></i>
                    </div>
                    <div class="feature-content">
                      <strong>Múltiplos tipos de mídia</strong>
                      <p class="mb-0 small text-muted">Texto, áudio, imagens e documentos</p>
                    </div>
                  </div>
                  <div class="feature-item">
                    <div class="feature-icon">
                      <i class="bi bi-person-check"></i>
                    </div>
                    <div class="feature-content">
                      <strong>Controle manual quando necessário</strong>
                      <p class="mb-0 small text-muted">Assuma a conversa a qualquer momento</p>
                    </div>
                  </div>
                </div>

                <div class="text-center">
                  <button @click="openConfigModal" class="btn btn-primary btn-lg px-5">
                    <i class="bi bi-gear me-2"></i>
                    Configurar WhatsApp
                  </button>
                  <p class="text-muted small mt-3 mb-0">
                    <i class="bi bi-info-circle me-1"></i>
                    Você precisará das credenciais do WhatsApp Business API
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="webhookVerified" class="whatsapp-layout">
      <div class="whatsapp-content">
        <ChatList
          :chats="chats"
          :selected-chat-id="selectedChatId"
          @select-chat="selectChat"
          @open-config="openConfigModal"
          @new-chat="openNewChatModal"
          @delete-chat="deleteChat"
          class="chat-list-panel"
        />

        <div v-if="selectedChatId" class="chat-window-panel">
          <ChatWindow
            :chat="selectedChat"
            :messages="currentMessages"
            :is-typing="isTyping"
            :is-mobile="isMobile"
            :place-id="place?.id_place"
            @send-message="handleSendMessage"
            @assign-chat="handleAssignChat"
            @chat-updated="handleChatUpdated"
            @back="selectedChatId = null"
          />
        </div>

        <div v-else class="empty-chat-state">
          <div class="empty-chat-content">
            <div class="empty-chat-icon">
              <i class="fab fa-whatsapp"></i>
            </div>
            <h4>Selecione uma conversa</h4>
            <p>Escolha uma conversa da lista para começar a conversar</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="webhook-pending-state">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-9 col-xl-8">
            <div class="card shadow-sm border-0">
              <div class="card-body p-5">
                <div class="text-center mb-4">
                  <div class="webhook-icon mb-4">
                    <i class="bi bi-link-45deg"></i>
                    <div class="icon-pulse-webhook"></div>
                  </div>
                  <div class="alert alert-success d-flex align-items-center justify-content-center mb-4">
                    <i class="bi bi-check-circle fs-3 me-2"></i>
                    <div class="text-start">
                      <strong class="d-block">Configuração salva com sucesso!</strong>
                      <span class="small">Aguardando ativação do webhook no painel do Facebook</span>
                    </div>
                  </div>
                  <h3 class="mb-3">Configure o webhook no Facebook Developers</h3>
                  <p class="text-muted mb-4">
                    Para começar a receber mensagens, você precisa configurar o webhook no painel do Facebook Developers.
                  </p>
                </div>

                <div class="webhook-info-card mb-4">
                  <h6 class="mb-3">
                    Informações necessárias:
                  </h6>
                  <div class="info-item">
                    <label>URL do Webhook:</label>
                    <div class="info-value">
                      <code>{{ webhookUrl }}</code>
                      <button @click="copyToClipboard(webhookUrl)" class="btn btn-sm btn-link p-0 ms-2" title="Copiar">
                        <i class="bi bi-copy"></i>
                      </button>
                    </div>
                  </div>
                  <div class="info-item">
                    <label>Verify Token:</label>
                    <div class="info-value">
                      <code>{{ verifyToken || 'Não configurado' }}</code>
                      <button v-if="verifyToken" @click="copyToClipboard(verifyToken)" class="btn btn-sm btn-link p-0 ms-2" title="Copiar">
                        <i class="bi bi-copy"></i>
                      </button>
                    </div>
                    <small class="text-muted d-block mt-1">Use este token quando o Facebook solicitar durante a configuração do webhook</small>
                  </div>
                </div>

                <div class="steps-guide mb-4">
                  <h6 class="mb-3">
                    <i class="bi bi-list-ol me-2"></i>
                    Passo a passo:
                  </h6>
                  <div class="step-item">
                    <div class="step-number">1</div>
                    <div class="step-content">
                      <strong>Acesse o Facebook Developers</strong>
                      <p class="mb-0 small text-muted">
                        Vá em <a href="https://developers.facebook.com/apps" target="_blank">developers.facebook.com/apps</a> 
                        e selecione seu app do WhatsApp Business
                      </p>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">2</div>
                    <div class="step-content">
                      <strong>Configure o webhook</strong>
                      <p class="mb-0 small text-muted">
                        Em "WhatsApp" > "Configuração" > "Webhook", clique em "Configurar webhooks" ou "Editar"
                      </p>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">3</div>
                    <div class="step-content">
                      <strong>Informe a URL e o Token</strong>
                      <p class="mb-0 small text-muted">
                        Cole a <strong>URL do Webhook</strong> acima e quando solicitado, informe o <strong>Verify Token</strong> também mostrado acima
                      </p>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">4</div>
                    <div class="step-content">
                      <strong>Verifique o status</strong>
                      <p class="mb-0 small text-muted">
                        Após configurar o webhook, clique em "Verificar Agora" para verificar se o webhook foi ativado com sucesso.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="text-center">
                  <div class="d-flex gap-2 justify-content-center flex-wrap">
                    <button @click="openConfigModal" class="btn btn-outline-primary">
                      <i class="bi bi-pencil me-2"></i>
                      Editar Configurações
                    </button>
                    <button @click="checkConnection" class="btn btn-primary" :disabled="checkingConnection">
                      <i class="bi bi bi-arrow-clockwise me-2"></i>
                      Verificar Agora
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <WhatsappConfigModal @config-saved="handleConfigSaved" />
    <NewChatModal
      v-if="place?.id_place"
      :place-id="place.id_place"
      @chat-created="handleChatCreated"
    />
  </div>
</template>

<script>
import ChatList from '@/components/whatsapp/ChatList.vue';
import ChatWindow from '@/components/whatsapp/ChatWindow.vue';
import WhatsappConfigModal from '@/components/whatsapp/WhatsappConfigModal.vue';
import NewChatModal from '@/components/whatsapp/NewChatModal.vue';
import whatsappService from '@/service/whatsappService';
import Alert from '@/helpers/alert';
import http from '@/helpers/http';
import Modal from '@/helpers/modal';

export default {
  name: 'WhatsAppIndex',
  components: {
    ChatList,
    ChatWindow,
    WhatsappConfigModal,
    NewChatModal,
  },
  data() {
    return {
      chats: [],
      selectedChatId: null,
      currentMessages: [],
      isTyping: false,
      connectionConfigured: false,
      webhookVerified: false,
      checkingConnection: true,
      isMobile: window.innerWidth < 768,
      echoChannel: null,
      verifyToken: null,
    };
  },
  async mounted() {
    await this.loadVerifyToken();
    await this.checkConnection();
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.disconnectWebSocket();
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },
    async loadVerifyToken() {
      if (!this.place?.id_place) return;

      const response = await http.get('/ai/whatsapp/informations', {
        params: { id_place: this.place.id_place }
      });

      this.verifyToken = response.data?.verify_token || null;
    },
    async checkConnection() {
      if (!this.place?.id_place) return;

      this.checkingConnection = true;
      const statusResponse = await http.get('/ai/whatsapp/status', {
        params: { id_place: this.place.id_place }
      });

      this.connectionConfigured = statusResponse.data.connected || false;
      this.webhookVerified = statusResponse.data.webhook_verified || false;
      this.checkingConnection = false;

      if (this.connectionConfigured && this.webhookVerified) {
        await this.loadChats();
        this.setupWebSocket();
      } else if (this.connectionConfigured && !this.webhookVerified) {
        await this.loadVerifyToken();
      }
    },
    async loadChats() {
      if (!this.place?.id_place) return;

      this.chats = await whatsappService.getChats(this.place.id_place);
    },
    async selectChat(chatId) {
      this.selectedChatId = chatId;
      await this.loadMessages(chatId);
    },
    async loadMessages(chatId) {
      if (!this.place?.id_place) return;

      const data = await whatsappService.getMessages(chatId, this.place.id_place);
      this.currentMessages = data.messages || [];
      
      if (data.chat) {
        const selectedChat = this.chats.find(c => c.id === chatId);
        if (selectedChat) {
          Object.assign(selectedChat, {
            isWithin24HourWindow: data.chat.isWithin24HourWindow,
          });
        }
      }
      
      const selectedChat = this.chats.find(c => c.id === chatId);
      if (selectedChat && data.chat) {
        Object.assign(selectedChat, {
          user_name: data.chat.user_name,
          user_photo: data.chat.user_photo,
          id_patient_user: data.chat.id_patient_user,
          assigned_to: data.chat.assigned_to,
        });
      }
    },
    async handleSendMessage(chatId, payload) {
      if (!this.place?.id_place) return;

      const replyToMessageId = payload.reply_to_message_id || null;

      if (payload.audioData) {
        await this.sendAudioMessage(chatId, payload.audioData, replyToMessageId);
      } else if (payload.file) {
        await this.sendMediaMessage(chatId, payload.file, payload.type, payload.fileName, replyToMessageId);
      } else {
        await whatsappService.sendMessage(
          chatId, 
          this.place.id_place, 
          payload.content, 
          payload.type, 
          null, 
          null, 
          null, 
          false, 
          replyToMessageId,
          payload.template_id,
          payload.template_parameters
        );
      }
    },
    async sendMediaMessage(chatId, file, type, fileName = null, replyToMessageId = null) {
      const uploadResult = await whatsappService.uploadMedia(chatId, this.place.id_place, file);
      const finalFileName = fileName || file?.name || file?.file?.name || 'documento';
      const content = type === 'document' ? finalFileName : '';
      await whatsappService.sendMessage(chatId, this.place.id_place, content, type, uploadResult.media_id, uploadResult.url, finalFileName, false, replyToMessageId);
    },
    async sendAudioMessage(chatId, audioData, replyToMessageId = null) {
      let fileName = 'audio.ogg';
      let mimeType = audioData.mimeType;
      
      if (mimeType.includes('webm')) {
        mimeType = 'audio/ogg';
        fileName = 'audio.ogg';
      } else if (mimeType.includes('opus')) {
        if (!mimeType.includes('ogg')) {
          mimeType = 'audio/opus';
          fileName = 'audio.opus';
        } else {
          mimeType = 'audio/ogg';
          fileName = 'audio.ogg';
        }
      } else if (mimeType.includes('mp3') || mimeType.includes('mpeg')) {
        mimeType = 'audio/mpeg';
        fileName = 'audio.mp3';
      } else if (mimeType.includes('mp4')) {
        mimeType = 'audio/mp4';
        fileName = 'audio.m4a';
      } else if (mimeType.includes('aac')) {
        mimeType = 'audio/aac';
        fileName = 'audio.aac';
      } else if (mimeType.includes('amr')) {
        mimeType = 'audio/amr';
        fileName = 'audio.amr';
      } else if (!mimeType.includes('ogg')) {
        mimeType = 'audio/ogg';
        fileName = 'audio.ogg';
      }
      
      try {
        const file = new File([audioData.blob], fileName, { type: mimeType });
        const uploadResult = await whatsappService.uploadMedia(chatId, this.place.id_place, file);
        await whatsappService.sendMessage(chatId, this.place.id_place, 'Áudio', 'audio', uploadResult.media_id, uploadResult.url, null, true, replyToMessageId);
      } catch (error) {
        console.error('Erro ao enviar áudio:', error);
        Alert.toastError('Erro ao enviar áudio. Tente novamente.');
        throw error;
      }
    },
    async handleAssignChat(chatId, assignedTo) {
      if (!this.place?.id_place) return;

      await whatsappService.assignChat(chatId, this.place.id_place, assignedTo);
      await this.loadChats();
    },
    async handleChatUpdated() {
      await this.loadChats();
      if (this.selectedChatId) {
        await this.loadMessages(this.selectedChatId);
      }
    },
    setupWebSocket() {
      if (!window.Echo || !this.place?.id_place) {
        setTimeout(() => this.setupWebSocket(), 1000);
        return;
      }

      const channelName = `whatsapp.place.${this.place.id_place}`;
      this.echoChannel = window.Echo.private(channelName);

      this.echoChannel
        .listen('WhatsAppMessageReceived', (data) => {
          if (data.chat_id === this.selectedChatId) {
            const exists = this.currentMessages.some(m => m.id === data.message.id);
            if (!exists) {
              this.currentMessages.push(data.message);
            }
          }
          this.loadChats();
        })
        .listen('WhatsAppMessageSent', (data) => {
          if (data.chat_id === this.selectedChatId) {
            const exists = this.currentMessages.some(m => m.id === data.message.id);
            if (!exists) {
              this.currentMessages.push(data.message);
            }
          }
          this.loadChats();
        })
        .listen('WhatsAppMessageDeleted', (data) => {
          if (data.chat_id === this.selectedChatId) {
            const index = this.currentMessages.findIndex(m => m.id === data.message_id);
            if (index !== -1) {
              this.currentMessages[index].deleted_at = new Date().toISOString();
            }
          }
        })
        .listen('WhatsAppMessageStatusUpdated', (data) => {
          if (data.chat_id === this.selectedChatId) {
            const index = this.currentMessages.findIndex(m => m.id === data.message_id);
            if (index !== -1) {
              const now = new Date().toISOString();
              switch(data.status) {
                case 'sent':
                  this.currentMessages[index].sent_at = now;
                  break;
                case 'delivered':
                  this.currentMessages[index].delivered_at = now;
                  break;
                case 'read':
                  this.currentMessages[index].read_at = now;
                  break;
                case 'failed':
                  this.currentMessages[index].failed_at = now;
                  break;
              }
            }
          }
        })
        .listen('WhatsAppTyping', (data) => {
          if (data.chat_id === this.selectedChatId) {
            this.isTyping = data.is_typing;
          }
        })
        .listen('WhatsAppChatUpdated', (data) => {
          if (data.chat_id === this.selectedChatId) {
            this.loadMessages(this.selectedChatId);
          }
          this.loadChats();
        });
    },
    disconnectWebSocket() {
      if (this.echoChannel) {
        window.Echo.leave(`whatsapp.place.${this.place.id_place}`);
        this.echoChannel = null;
      }
    },
    openConfigModal() {
      Modal.toggle('#whatsappConfigModal');
    },
    async handleConfigSaved() {
      await this.loadVerifyToken();
      await this.checkConnection();
    },
    openNewChatModal() {
      Modal.toggle('#newChatModal');
    },
    handleChatCreated(chat) {
      this.loadChats();
      this.selectChat(chat.id);
    },
    async deleteChat(chatId) {
      if (!this.place?.id_place) return;

      try {
        await whatsappService.deleteChat(this.place.id_place, chatId);
        Alert.toastSuccess('Conversa deletada com sucesso!');
        
        if (this.selectedChatId === chatId) {
          this.selectedChatId = null;
          this.currentMessages = [];
        }
        
        await this.loadChats();
      } catch (error) {
        console.error('Erro ao deletar conversa:', error);
        Alert.toastError('Erro ao deletar conversa. Tente novamente.');
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        Alert.toastSuccess('Copiado para a área de transferência!');
      }).catch(() => {
        Alert.toastError('Erro ao copiar');
      });
    },
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    selectedChat() {
      return this.chats.find(c => c.id === this.selectedChatId);
    },
    webhookUrl() {
      return `https://api.ubbimed.com.br/externals/whatsapp/webhook`;
    },
  },
}
</script>

<style scoped>
.whatsapp-container {
  height: calc(100vh - 130px);
  display: flex;
  flex-direction: column;
  background: #f5f8fa;
  overflow: hidden;
  position: relative;
}

.loading-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f8fa;
}

.loading-content {
  text-align: center;
  padding: 2rem;
}

.empty-config-state,
.webhook-pending-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f8fa;
  padding: 260px 20px 40px 20px;
  overflow-y: auto;
}

.whatsapp-icon-wrapper {
  position: relative;
  display: inline-block;
}

.whatsapp-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.5rem;
  color: white;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.25);
  position: relative;
  z-index: 2;
}

.icon-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(37, 211, 102, 0.2);
  animation: pulse 2s infinite;
  z-index: 1;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0;
  }
}

.empty-config-title {
  color: #181c32;
  font-weight: 700;
  font-size: 1.75rem;
}

.empty-config-description {
  color: #5e6278;
  font-size: 1rem;
  line-height: 1.6;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e4e6ef;
  transition: all 0.2s;
}

.feature-item:hover {
  background: #f5f8fa;
  border-color: #780f5a;
  transform: translateX(4px);
}

.feature-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #780f5a 0%, #9d4779 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.feature-content strong {
  color: #181c32;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}

.card {
  border-radius: 12px;
}

.alert-warning {
  background-color: #fff4de;
  border-color: #ffc700;
  color: #7c4a00;
}

.alert-warning strong {
  color: #7c4a00;
}

.btn-primary {
  background-color: #780f5a;
  border-color: #780f5a;
}

.btn-primary:hover {
  background-color: #5d0c47;
  border-color: #5d0c47;
}

.whatsapp-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.whatsapp-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.chat-list-panel {
  width: 350px;
  flex-shrink: 0;
}

.chat-window-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.empty-chat-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f8fa;
}

.empty-chat-content {
  text-align: center;
  color: #5e6278;
}

.empty-chat-icon {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
  margin: 0 auto 24px;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.2);
}

.empty-chat-content h4 {
  color: #181c32;
  font-weight: 600;
  margin-bottom: 8px;
}

.webhook-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #780f5a 0%, #9d4779 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
  margin: 0 auto;
  box-shadow: 0 8px 24px rgba(120, 15, 90, 0.2);
  position: relative;
}

.icon-pulse-webhook {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(120, 15, 90, 0.2);
  animation: pulseWebhook 2s infinite;
}

@keyframes pulseWebhook {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

.webhook-info-card {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e4e6ef;
}

.info-item {
  margin-bottom: 20px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item label {
  display: block;
  font-weight: 600;
  color: #181c32;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.info-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-value code {
  background: rgba(120, 15, 90, 0.1);
  padding: 10px 14px;
  border-radius: 6px;
  color: #780f5a;
  font-weight: 600;
  font-size: 0.9rem;
  flex: 1;
  word-break: break-all;
  border: 1px solid rgba(120, 15, 90, 0.2);
}

.info-value .btn-link {
  color: #780f5a;
  text-decoration: none;
}

.info-value .btn-link:hover {
  color: #5d0c47;
}

.steps-guide {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e4e6ef;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.step-item:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #780f5a 0%, #9d4779 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
  text-align: left;
}

.step-content strong {
  color: #181c32;
  display: block;
  margin-bottom: 4px;
}

.alert-light code {
  background: rgba(120, 15, 90, 0.1);
  padding: 8px 12px;
  border-radius: 4px;
  color: #780f5a;
  font-weight: 500;
  font-size: 0.85rem;
}

.alert-info {
  background-color: #e1f0ff;
  border-color: #b3d9ff;
  color: #004085;
}

@media (max-width: 767px) {
  .chat-list-panel {
    width: 100%;
  }

  .chat-window-panel {
    display: none;
  }

  .chat-window-panel.active {
    display: flex;
  }
}
</style>

